import React, { useMemo } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ReactTooltip from 'react-tooltip';

import CardDesignerCardElementPlaceholder from '../CardDesignerCardElementPlaceholder';
import PhotoSlotIcon from '../../../../../icons/PhotoSlotIcon';
import { PHOTO_TYPE } from '../../../../../../services/cards/constants';
import LogoIcon from '../../../../../icons/LogoIcon';
import Badge from '../../../../Badge';

const propTypes = {
  element: PropTypes.shape({
    id: PropTypes.string,
    cardElement: PropTypes.shape({
      frame: PropTypes.shape({
        width: PropTypes.number,
        height: PropTypes.number,
      }),
    }),
    cardElementConfiguration: PropTypes.shape({
      asset_image: PropTypes.shape({
        id: PropTypes.string,
      }),
    }),
  }),
  className: PropTypes.string,
  onClick: PropTypes.func.isRequired,
  cardBounds: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number,
  }),
  helperText: PropTypes.node,
};

const CardDesignerCardElementPhoto = ({
  element,
  className,
  onClick,
  cardBounds,
  helperText,
}) => {
  const { cardElement, cardElementConfiguration } = element;
  const { help_placeholder: helpPlaceholder } = cardElementConfiguration ?? {};
  const { asset_image: assetImage, photo_type: photoType } =
    cardElementConfiguration ?? {};
  const classes = classNames({
    'card-designer-card-element': true,
    'card-designer-card-element-photo': true,
    [className]: !!className,
  });

  const Icon = useMemo(() => {
    if (photoType === PHOTO_TYPE.LOGO) {
      return <LogoIcon color="#798588" />;
    }

    return <PhotoSlotIcon />;
  }, [photoType]);

  // A dynamic logo slot is filled at order time, so the placeholder carries no
  // artwork to explain itself — the tooltip says what it is and what feeds it.
  const isDynamicLogo = photoType === PHOTO_TYPE.LOGO;
  const tooltipId = `card-element-photo-tip-${element?.id ?? cardElement?.id}`;

  const frame = cardElement?.frame;
  const minWidth =
    frame && cardBounds?.width
      ? Math.floor(frame.width * cardBounds.width * 300)
      : null;
  const minHeight =
    frame && cardBounds?.height
      ? Math.floor(frame.height * cardBounds.height * 300)
      : null;

  const tipVisible = !assetImage && isDynamicLogo;

  return (
    <a
      href="#"
      className={classes}
      onClick={event => {
        event.preventDefault();
        onClick();
      }}
      data-tip={tipVisible ? '' : undefined}
      data-for={tipVisible ? tooltipId : undefined}
      data-place="top"
      // Anchored to the slot; the default float effect re-positions the tip on
      // every mousemove.
      data-effect="solid"
      // Driven explicitly rather than left to the library's own listener
      // binding, which scans the document on mount and so races the portal.
      onMouseEnter={event => {
        if (tipVisible) {
          ReactTooltip.show(event.currentTarget);
        }
      }}
      onMouseLeave={event => {
        if (tipVisible) {
          ReactTooltip.hide(event.currentTarget);
        }
      }}
    >
      {!assetImage && (
        <CardDesignerCardElementPlaceholder
          icon={
            <div className="d-flex flex-column align-items-center text-light text-xs">
              {Icon}
              {!helperText && !isDynamicLogo && minWidth && minHeight && (
                <div className="mt-1 text-center px-2">
                  Minimum suggested resolution:
                  <br />
                  {minWidth}px x {minHeight}px
                </div>
              )}
            </div>
          }
          helperText={helperText}
          iconClassName="card-designer-card-element-photo-icon"
        />
      )}
      {/* Portalled to the body: the card clips its overflow, which would hide
          a tooltip rendered in place. */}
      {tipVisible &&
        createPortal(
          <ReactTooltip
            id={tooltipId}
            className="tooltip card-designer-card-element-tooltip"
          >
            <p className="text-center mb-0">
              Dynamic Logo placeholder — the logo set in Gallery or Album
              Settings &rarr; General is inserted here for each print. No logo
              set there means no logo on the print.
            </p>
            {!!minWidth && !!minHeight && (
              <p className="text-center mb-0 mt-1">
                Minimum suggested resolution: {minWidth}px x {minHeight}px
              </p>
            )}
          </ReactTooltip>,
          document.body,
        )}
      {!!helpPlaceholder && (
        <Badge badgeSize="xs" badgeStyle="info">
          {helpPlaceholder}
        </Badge>
      )}
    </a>
  );
};

CardDesignerCardElementPhoto.propTypes = propTypes;

export default CardDesignerCardElementPhoto;
